import type { StateUpdater } from '../../../canvas/core';
import type { PageId } from '../coordinates/geometry';
import type { PageDimensions } from '../layout/types';
import type { PageLayoutSlice, PageState } from './types';

const DEFAULT_PAGE_STATE: Omit<PageState, 'dimensions'> = {
  visible: false,
};

export const initializePage =
  <Root extends PageLayoutSlice>(
    pageId: PageId,
    dimensions: PageDimensions,
    initialState: Partial<Omit<PageState, 'dimensions'>> = {}
  ): StateUpdater<Root> =>
  state => ({
    ...state,
    pageLayout: {
      ...state.pageLayout,
      list: {
        ...state.pageLayout.list,
        [pageId]: {
          dimensions,
          ...DEFAULT_PAGE_STATE,
          ...initialState,
        },
      },
    },
  });

export const setPageDimensions =
  <Root extends PageLayoutSlice>(
    pageId: PageId,
    dimensions: PageDimensions
  ): StateUpdater<Root> =>
  state => {
    const page = state.pageLayout.list[pageId];
    if (!page) return state;

    return {
      ...state,
      pageLayout: {
        ...state.pageLayout,
        list: {
          ...state.pageLayout.list,
          [pageId]: {
            ...page,
            dimensions,
          },
        },
      },
    };
  };

export const setPageVisible =
  <Root extends PageLayoutSlice>(
    pageId: PageId,
    visible: boolean
  ): StateUpdater<Root> =>
  state => {
    const page = state.pageLayout.list[pageId];
    if (!page) return state;

    return {
      ...state,
      pageLayout: {
        ...state.pageLayout,
        list: {
          ...state.pageLayout.list,
          [pageId]: {
            ...page,
            visible,
          },
        },
      },
    };
  };

export const setActivePage =
  <Root extends PageLayoutSlice>(pageId: PageId | null): StateUpdater<Root> =>
  state => ({
    ...state,
    pageLayout: {
      ...state.pageLayout,
      activeId: pageId,
    },
  });

export const setPageOrder =
  <Root extends PageLayoutSlice>(pageOrder: PageId[]): StateUpdater<Root> =>
  state => ({
    ...state,
    pageLayout: {
      ...state.pageLayout,
      order: pageOrder,
    },
  });
